/**
 * Detect that the viewer was booted from a game because their time expired and an
 * AI took over their seat (server: apps/api/src/lib/aiTakeover.ts). Pure so it's testable.
 *
 * Two ways the app learns it:
 *  - live: the GAME_UPDATED push lists a bot seat whose takenOverFromUserId is the viewer;
 *  - returning later: GET /games/:id answers 403 with code AI_TAKEOVER (no game state).
 */
import type { GameResponse } from '@warcarp/api-types'
import { HttpError } from './api'

export interface TimeExpiredNotice {
  gameName: string | null
  botName: string | null
  /** 'abandoned' when the player chose Abandon; otherwise their time expired. */
  reason: 'turn-timeout' | 'abandoned'
}

export function takeoverNoticeForViewer(
  game: Pick<GameResponse, 'name' | 'players'> | null | undefined,
  viewerUserId: string | null | undefined,
): TimeExpiredNotice | null {
  if (!game || !viewerUserId) return null
  const seat = game.players.find((p) => p.takenOverFromUserId === viewerUserId)
  return seat
    ? { gameName: game.name ?? null, botName: seat.displayName, reason: seat.takenOverReason === 'abandoned' ? 'abandoned' : 'turn-timeout' }
    : null
}

export function takeoverNoticeFromError(error: unknown): TimeExpiredNotice | null {
  if (!(error instanceof HttpError) || error.status !== 403 || error.code !== 'AI_TAKEOVER') return null
  const body = error.body ?? {}
  return {
    gameName: typeof body.gameName === 'string' ? body.gameName : null,
    botName: typeof body.botName === 'string' ? body.botName : null,
    reason: body.takenOverReason === 'abandoned' ? 'abandoned' : 'turn-timeout',
  }
}
